/**
 * Temperature Service — NASA GISTEMP v4 Integration
 *
 * Global average surface temperature is fetched from the NASA GISS Surface
 * Temperature Analysis (GISTEMP v4) monthly CSV, which is the dataset cited
 * by WMO, IPCC, and all major climate agencies. No API key required.
 *
 * Dataset:   https://data.giss.nasa.gov/gistemp/tabledata_v4/GLB.Ts+dSST.csv
 * Baseline:  1951–1980 mean (≈ 14.0 °C absolute)
 * Updated:   Monthly by NASA GISS
 *
 * Ice melt data uses the IMBIE 2023 consortium estimates — the authoritative
 * peer-reviewed source for ice-sheet mass balance. Global ice-sheet loss is
 * published annually; a static constant is used and should be updated when
 * a new IMBIE report is released.
 *
 * IMBIE reference: https://imbie.org/imbie-3/
 */

import { supabase } from "@/integrations/supabase/client";
import { getUserLocation } from "@/services/locationService";

// ─── Interfaces ───────────────────────────────────────────────────────────────

type ClimateReadingSource = "live" | "cached" | "fallback";

export interface TemperatureReading {
  value: number;
  unit: "celsius" | "fahrenheit";
  source: ClimateReadingSource;
  timestamp: string;
  location?: string;
  /** Human-readable attribution, e.g. "NASA GISTEMP v4 · Jan 2026" */
  label?: string;
}

export interface IceMeltingReading {
  rate_tons_annually: number;
  rate_gigatons_annually: number;
  source: ClimateReadingSource;
  timestamp: string;
  /** Human-readable attribution */
  label?: string;
}

export interface GlobalTemperatureData {
  id: string;
  temperature_celsius: number;
  temperature_fahrenheit: number;
  ice_melting_rate_tons_annually?: number;
  ice_melting_rate_gigatons_annually?: number;
  data_source: string;
  fetch_date: string;
  created_at: string;
  updated_at: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** GISTEMP anomalies are relative to the 1951-1980 mean. The absolute mean
 *  for that period is ≈ 14.0 °C per NASA / WMO convention. */
const GISTEMP_BASELINE_CELSIUS = 14.0;

const GISTEMP_CSV_URL =
  "https://data.giss.nasa.gov/gistemp/tabledata_v4/GLB.Ts+dSST.csv";

const CORS_PROXY = "https://corsproxy.io/?";

/** Supabase cache TTL: fetch fresh GISTEMP data at most once per 30 days. */
const CACHE_DURATION_DAYS = 30;

/**
 * IMBIE 2023 Phase 3 consensus ice-sheet mass balance estimates.
 *
 * Greenland ice sheet (2006–2020):  ~267 Gt/yr  (IMBIE 2023)
 * Antarctic ice sheet (1992–2020):  ~149 Gt/yr  (IMBIE 2023)
 * ─────────────────────────────────────────────
 * Total ice-sheet loss:             ~416 Gt/yr
 *
 * Note: This excludes mountain glaciers (~335 Gt/yr, Hugonnet et al. 2021).
 * Update this constant when a new IMBIE consortium report is published.
 */
const IMBIE_ICE_SHEET_LOSS_GT = 416;
const IMBIE_ICE_SHEET_LOSS_TONS = IMBIE_ICE_SHEET_LOSS_GT * 1_000_000_000;
const IMBIE_LABEL = "IMBIE 2023 (Greenland + Antarctica)";
const TEMPERATURE_FALLBACK_CELSIUS = 15.17;
const TEMPERATURE_FALLBACK_FAHRENHEIT =
  (TEMPERATURE_FALLBACK_CELSIUS * 9) / 5 + 32;

// ─── GISTEMP CSV Parser ───────────────────────────────────────────────────────

interface GISTEMPRecord {
  year: number;
  annualAnomaly: number | null;
  latestMonthAnomaly: number | null;
  /** Period string like "2025" or "Jan 2026" */
  period: string;
}

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Parse NASA GISTEMP v4 global CSV and return the most recent record.
 *
 * CSV format (comma-separated):
 *   Header lines (not starting with 4-digit year) are skipped.
 *   Data rows: Year,Jan,Feb,...,Dec,J-D,D-N,DJF,MAM,JJA,SON
 *   Missing values are represented as "****"
 */
function parseGISTEMP(csvText: string): GISTEMPRecord | null {
  const lines = csvText.split("\n");
  let lastRecord: GISTEMPRecord | null = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!/^\d{4},/.test(trimmed)) continue;

    const cols = trimmed.split(",");
    const year = parseInt(cols[0], 10);
    if (isNaN(year)) continue;

    // cols[13] = J-D annual mean. Guard with isFinite to catch NaN from
    // partial-year rows where NASA may use "***" or similar non-"****" markers.
    const annualRaw = cols[13]?.trim();
    const annualParsed = annualRaw ? parseFloat(annualRaw) : NaN;
    const annualAnomaly = isFinite(annualParsed) ? annualParsed : null;

    // Find the last non-missing month value (cols[1]–cols[12])
    let latestMonthAnomaly: number | null = null;
    let latestMonthIndex = -1;
    for (let m = 11; m >= 0; m--) {
      const raw = cols[m + 1]?.trim();
      if (raw) {
        const val = parseFloat(raw);
        if (isFinite(val)) {
          latestMonthAnomaly = val;
          latestMonthIndex = m;
          break;
        }
      }
    }

    let period: string;
    if (annualAnomaly !== null) {
      period = String(year);
    } else if (latestMonthIndex >= 0) {
      period = `${MONTH_NAMES[latestMonthIndex]} ${year}`;
    } else {
      console.warn(
        `[GISTEMP] Skipping row for ${year}: no valid anomaly values. cols=${JSON.stringify(cols.slice(0, 14))}`,
      );
      continue;
    }

    lastRecord = { year, annualAnomaly, latestMonthAnomaly, period };
  }

  return lastRecord;
}

// ─── Service Class ────────────────────────────────────────────────────────────

class TemperatureService {
  // ── Supabase cache helpers ────────────────────────────────────────────────

  /** Returns true if the cached row should NOT be trusted as a valid reading. */
  private isInvalidCache(data: GlobalTemperatureData | null): boolean {
    if (!data) return true;
    const VALID_SOURCES = ["nasa_gistemp_v4", "nasa_gistemp_v4_fallback"];
    if (!VALID_SOURCES.includes(data.data_source)) return true;
    if (
      !isFinite(data.temperature_celsius) ||
      !isFinite(data.temperature_fahrenheit)
    )
      return true;
    return false;
  }

  /** @deprecated use isInvalidCache */
  private isSeededEstimate(data: GlobalTemperatureData | null): boolean {
    return this.isInvalidCache(data);
  }

  private getCacheTimestamp(fetchDate?: string): string {
    if (!fetchDate) return new Date().toISOString();
    return new Date(fetchDate).toISOString();
  }

  private async getCachedData(): Promise<GlobalTemperatureData | null> {
    try {
      const { data, error } = await supabase
        .from("global_temperature_data")
        .select("*")
        .order("fetch_date", { ascending: false })
        .limit(1);
      if (error || !data || data.length === 0) return null;
      return data[0] as unknown as GlobalTemperatureData;
    } catch {
      return null;
    }
  }

  private async needsNewData(): Promise<boolean> {
    try {
      const cached = await this.getCachedData();
      if (!cached) return true;
      const daysSince = Math.floor(
        (Date.now() - new Date(cached.fetch_date).getTime()) /
          (1000 * 60 * 60 * 24),
      );
      return daysSince >= CACHE_DURATION_DAYS;
    } catch {
      return true;
    }
  }

  private async storeUnifiedData(
    tempCelsius: number,
    tempFahrenheit: number,
    iceTons = IMBIE_ICE_SHEET_LOSS_TONS,
    iceGigatons = IMBIE_ICE_SHEET_LOSS_GT,
    dataSource = "nasa_gistemp_v4",
  ): Promise<void> {
    try {
      const currentTime = new Date().toISOString();
      const { data: latest, error } = await supabase
        .from("global_temperature_data")
        .select(
          "id, ice_melting_rate_tons_annually, ice_melting_rate_gigatons_annually",
        )
        .order("fetch_date", { ascending: false })
        .limit(1);

      if (error || !latest || latest.length === 0) {
        console.log(
          "[TemperatureService] No existing row found — attempting INSERT.",
        );
        const { data: insertData, error: insertError } = await supabase
          .from("global_temperature_data")
          .insert({
            temperature_celsius: tempCelsius,
            temperature_fahrenheit: tempFahrenheit,
            ice_melting_rate_tons_annually: iceTons,
            ice_melting_rate_gigatons_annually: iceGigatons,
            data_source: dataSource,
            fetch_date: currentTime,
          } as any)
          .select();
        if (insertError) {
          console.error(
            "[TemperatureService] INSERT failed — likely RLS policy blocking anonymous writes. Error:",
            insertError,
          );
          console.error(
            "[TemperatureService] Fix: add an RLS policy on global_temperature_data allowing INSERT/UPDATE for the service_role or authenticated users.",
          );
        } else {
          console.log(
            "[TemperatureService] INSERT succeeded. Rows returned:",
            insertData,
          );
        }
      } else {
        const existing = latest[0] as any;
        console.log(
          `[TemperatureService] Existing row found (id: ${existing.id}) — attempting UPDATE.`,
        );
        const { data: updateData, error: updateError } = await supabase
          .from("global_temperature_data")
          .update({
            temperature_celsius: tempCelsius,
            temperature_fahrenheit: tempFahrenheit,
            ice_melting_rate_tons_annually: iceTons,
            ice_melting_rate_gigatons_annually: iceGigatons,
            data_source: dataSource,
            fetch_date: currentTime,
            updated_at: currentTime,
          } as any)
          .eq("id", existing.id)
          .select();
        if (updateError) {
          console.error(
            "[TemperatureService] UPDATE failed — likely RLS policy blocking writes. Error:",
            updateError,
          );
          console.error(
            "[TemperatureService] Fix: ensure RLS policy on global_temperature_data allows UPDATE for anon or authenticated role.",
          );
        } else if (!updateData || updateData.length === 0) {
          console.error(
            `[TemperatureService] UPDATE matched 0 rows — RLS silently blocked the write. ` +
              `Row id=${existing.id} exists but the current user lacks UPDATE permission. ` +
              `Fix: in Supabase Dashboard → Authentication → Policies → global_temperature_data, ` +
              `add an UPDATE policy for the 'anon' role, or use the service_role key server-side.`,
          );
        } else {
          console.log(
            "[TemperatureService] UPDATE succeeded. Rows affected:",
            updateData.length,
            updateData,
          );
        }
      }
    } catch (err) {
      console.error("[TemperatureService] storeUnifiedData error:", err);
    }
  }

  private async syncIceDataIntoCache(): Promise<GlobalTemperatureData | null> {
    try {
      const cached = await this.getCachedData();
      const currentTime = new Date().toISOString();

      const VALID_SOURCES = ["nasa_gistemp_v4", "nasa_gistemp_v4_fallback"];
      const resolvedSource =
        cached && VALID_SOURCES.includes(cached.data_source)
          ? cached.data_source
          : "nasa_gistemp_v4_fallback";

      const temperatureCelsius =
        cached && !this.isSeededEstimate(cached)
          ? cached.temperature_celsius
          : TEMPERATURE_FALLBACK_CELSIUS;
      const temperatureFahrenheit =
        cached && !this.isSeededEstimate(cached)
          ? cached.temperature_fahrenheit
          : TEMPERATURE_FALLBACK_FAHRENHEIT;

      if (!cached) {
        await supabase.from("global_temperature_data").insert({
          temperature_celsius: temperatureCelsius,
          temperature_fahrenheit: temperatureFahrenheit,
          ice_melting_rate_tons_annually: IMBIE_ICE_SHEET_LOSS_TONS,
          ice_melting_rate_gigatons_annually: IMBIE_ICE_SHEET_LOSS_GT,
          data_source: "nasa_gistemp_v4_fallback",
          fetch_date: currentTime,
        } as any);
      } else {
        await supabase
          .from("global_temperature_data")
          .update({
            temperature_celsius: temperatureCelsius,
            temperature_fahrenheit: temperatureFahrenheit,
            ice_melting_rate_tons_annually: IMBIE_ICE_SHEET_LOSS_TONS,
            ice_melting_rate_gigatons_annually: IMBIE_ICE_SHEET_LOSS_GT,
            data_source: resolvedSource,
            fetch_date: currentTime,
            updated_at: currentTime,
          } as any)
          .eq("id", cached.id);
      }

      return this.getCachedData();
    } catch (err) {
      console.error("[TemperatureService] syncIceDataIntoCache error:", err);
      return null;
    }
  }

  // ── NASA GISTEMP fetch ────────────────────────────────────────────────────

  private async fetchGISTEMPAnomaly(): Promise<GISTEMPRecord | null> {
    try {
      const url = CORS_PROXY + encodeURIComponent(GISTEMP_CSV_URL);
      console.log("[GISTEMP] Requesting:", url);
      const res = await fetch(url, { cache: "no-store" });
      console.log(`[GISTEMP] Response: HTTP ${res.status} ${res.statusText}`);
      if (!res.ok) {
        console.error(`[GISTEMP] HTTP error — aborting fetch.`);
        return null;
      }
      const text = await res.text();
      // Log last 3 data rows of the CSV to diagnose partial-year formatting
      const dataLines = text
        .split("\n")
        .filter((l) => /^\d{4},/.test(l.trim()));
      console.log("[GISTEMP] Last 3 CSV data rows:", dataLines.slice(-3));
      const record = parseGISTEMP(text);
      if (record) {
        const usedAnomaly = record.annualAnomaly ?? record.latestMonthAnomaly;
        console.log(
          `[GISTEMP] Parsed record — year: ${record.year}, period: "${record.period}", annualAnomaly: ${record.annualAnomaly}, latestMonthAnomaly: ${record.latestMonthAnomaly}, usedAnomaly: ${usedAnomaly}`,
        );
        if (!isFinite(usedAnomaly as number)) {
          console.error(
            "[GISTEMP] Parsed anomaly is not finite — will not store NaN to DB.",
          );
          return null;
        }
      } else {
        console.warn(
          "[GISTEMP] parseGISTEMP returned null — no valid record found in CSV.",
        );
      }
      return record;
    } catch (err) {
      console.error("[GISTEMP] Fetch error:", err);
      return null;
    }
  }

  // ── Unit preference ───────────────────────────────────────────────────────

  private async getTemperatureUnit(
    userId?: string,
  ): Promise<"celsius" | "fahrenheit"> {
    try {
      if (userId) {
        const location = await getUserLocation(userId);
        const fahrenheitCountries = [
          "United States",
          "Liberia",
          "Myanmar",
          "USA",
          "US",
        ];
        if (
          fahrenheitCountries.some((c) =>
            location.country.toLowerCase().includes(c.toLowerCase()),
          )
        ) {
          return "fahrenheit";
        }
      }
    } catch {
      // fall through
    }
    return "celsius";
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /**
   * Get the current global average surface temperature from NASA GISTEMP v4.
   *
   * Priority:
   *   1. Live GISTEMP CSV (if cache stale or forceRefresh=true)
   *   2. Supabase-cached value from a prior successful fetch
   *   3. Hardcoded 2024 annual mean fallback (15.17 °C, GISTEMP v4 2024)
   */
  async getCurrentGlobalTemperature(
    userId?: string,
    forceRefresh = false,
  ): Promise<TemperatureReading> {
    const preferredUnit = await this.getTemperatureUnit(userId);

    const toReading = (
      celsius: number,
      source: TemperatureReading["source"],
      label: string,
      timestamp = new Date().toISOString(),
    ): TemperatureReading => ({
      value: preferredUnit === "celsius" ? celsius : (celsius * 9) / 5 + 32,
      unit: preferredUnit,
      source,
      timestamp,
      label,
      location: "Global",
    });

    const cached = await this.getCachedData();
    const cacheInvalid = this.isInvalidCache(cached);
    const cacheStale = await this.needsNewData();

    // Attempt live fetch when: forced, cache stale, or cache corrupted (e.g. groq_api row)
    if (forceRefresh || cacheStale || cacheInvalid) {
      console.log("[GISTEMP] Fetching fresh data from NASA GISS...");
      const record = await this.fetchGISTEMPAnomaly();

      if (record) {
        const anomaly = record.annualAnomaly ?? record.latestMonthAnomaly ?? 0;
        const celsius = GISTEMP_BASELINE_CELSIUS + anomaly;
        if (!isFinite(celsius)) {
          console.error(
            `[GISTEMP] Computed celsius is not finite (${celsius}) — skipping Supabase write, falling through to cache/fallback.`,
          );
        } else {
          console.log(
            `[GISTEMP] Writing to Supabase — celsius: ${celsius}, fahrenheit: ${(celsius * 9) / 5 + 32}`,
          );
          await this.storeUnifiedData(celsius, (celsius * 9) / 5 + 32);
          return toReading(
            celsius,
            "live",
            `NASA GISTEMP v4 · ${record.period}`,
          );
        }
      }

      console.warn(
        "[GISTEMP] Live fetch returned no usable record, trying cache...",
      );

      // Live fetch failed and cache is corrupt — overwrite bad row with fallback
      // so data_source, fetch_date, and updated_at are all corrected in Supabase
      if (cacheInvalid) {
        console.warn(
          "[GISTEMP] Overwriting invalid cache row with fallback values.",
        );
        await this.storeUnifiedData(
          TEMPERATURE_FALLBACK_CELSIUS,
          TEMPERATURE_FALLBACK_FAHRENHEIT,
          IMBIE_ICE_SHEET_LOSS_TONS,
          IMBIE_ICE_SHEET_LOSS_GT,
          "nasa_gistemp_v4_fallback",
        );
      }
    }

    // Use valid cached row
    if (!cacheInvalid && cached) {
      return toReading(
        cached.temperature_celsius,
        "cached",
        "NASA GISTEMP v4 (cached in Supabase)",
        this.getCacheTimestamp(cached.fetch_date),
      );
    }

    // Hardcoded fallback — GISTEMP v4 2024 annual mean (+1.17 °C anomaly)
    console.warn("[GISTEMP] Using hardcoded 2024 fallback.");
    return toReading(
      TEMPERATURE_FALLBACK_CELSIUS,
      "fallback",
      "NASA GISTEMP v4 · 2024 (fallback)",
    );
  }

  /**
   * Get the global ice-sheet mass balance rate.
   *
   * Uses the IMBIE 2023 consortium consensus value (Greenland + Antarctica).
   * Ice-sheet mass balance is published annually; this constant should be
   * updated when a new IMBIE Phase report is released.
   */
  async getCurrentIceMeltingRate(
    forceRefresh = false,
  ): Promise<IceMeltingReading> {
    let cached = await this.getCachedData();

    if (
      forceRefresh ||
      !cached ||
      this.isSeededEstimate(cached) ||
      !cached.ice_melting_rate_tons_annually ||
      !cached.ice_melting_rate_gigatons_annually
    ) {
      cached = await this.syncIceDataIntoCache();
    }

    if (
      cached?.ice_melting_rate_tons_annually &&
      cached?.ice_melting_rate_gigatons_annually
    ) {
      return {
        rate_tons_annually: cached.ice_melting_rate_tons_annually,
        rate_gigatons_annually: cached.ice_melting_rate_gigatons_annually,
        source: "cached",
        timestamp: this.getCacheTimestamp(cached.fetch_date),
        label: "IMBIE 2023 (cached in Supabase)",
      };
    }

    return {
      rate_tons_annually: IMBIE_ICE_SHEET_LOSS_TONS,
      rate_gigatons_annually: IMBIE_ICE_SHEET_LOSS_GT,
      source: "fallback",
      timestamp: new Date().toISOString(),
      label: `${IMBIE_LABEL} (fallback)`,
    };
  }

  /** Force-refresh temperature (convenience method used by the UI refresh button). */
  async refreshTemperatureData(userId?: string): Promise<TemperatureReading> {
    return this.getCurrentGlobalTemperature(userId, true);
  }

  /** Force-sync ice data into Supabase and return the updated reading. */
  async refreshIceMeltingData(): Promise<IceMeltingReading> {
    return this.getCurrentIceMeltingRate(true);
  }

  /** Unit conversion utility. */
  static convertTemperature(
    value: number,
    fromUnit: "celsius" | "fahrenheit",
    toUnit: "celsius" | "fahrenheit",
  ): number {
    if (fromUnit === toUnit) return value;
    if (fromUnit === "celsius") return (value * 9) / 5 + 32;
    return ((value - 32) * 5) / 9;
  }
}

// Export singleton instance
export const temperatureService = new TemperatureService();
export default TemperatureService;
